import React from "react";
import { Edit, useForm } from "@refinedev/antd";
import { Form, Input, Switch, Button, Space, Typography } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { FormListFieldData, FormListOperation } from "antd/es/form/FormList";

const { Title } = Typography;

interface VendorContactDTO {
    phoneNumber: string;
    email: string;
    isPrimary: boolean;
}

interface VendorAddressDTO {
    houseNo: string;
    street: string;
    city: string;
    province: string;
    country: string;
    isPrimary: boolean;
}

interface EditVendorDTO {
    id: string;
    tradeName: string;
    vatNumber: string;
    tpin: string;
    name: string;
    description?: string;
    bankAccount: string;
    bankName: string;
    swiftCode: string;
    isActive: boolean;
    vendorContacts: VendorContactDTO[];
    vendorAddresses: VendorAddressDTO[];
}

export const VendorEdit: React.FC = () => {
    const { formProps, saveButtonProps } = useForm({
        resource: "vendors",
        successNotification: {
            message: "Vendor updated successfully",
            type: "success",
        },
    });

    return (
        <Edit saveButtonProps={saveButtonProps}>
            <Form {...formProps} layout="vertical">
                <Form.Item
                    label="Trade Name"
                    name="tradeName"
                    rules={[{ required: true, message: 'Please input trade name' }]}
                >
                    <Input id="tradeName" />
                </Form.Item>

                <Form.Item
                    label="VAT Number"
                    name="vatNumber"
                >
                    <Input id="vatNumber" />
                </Form.Item>

                <Form.Item
                    label="TIN Number"
                    name="tpin"
                >
                    <Input id="tpin" />
                </Form.Item>

                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Please input name' }]}
                >
                    <Input id="name" />
                </Form.Item>

                <Form.Item
                    label="Description"
                    name="description"
                >
                    <Input.TextArea id="description" />
                </Form.Item>

                <Form.Item
                    label="Bank Account"
                    name="bankAccount"
                >
                    <Input id="bankAccount" />
                </Form.Item>

                <Form.Item
                    label="Bank Name"
                    name="bankName"
                >
                    <Input id="bankName" />
                </Form.Item>

                <Form.Item
                    label="Swift Code"
                    name="swiftCode"
                >
                    <Input id="swiftCode" />
                </Form.Item>

                <Form.Item
                    label="Active"
                    name="isActive"
                    valuePropName="checked"
                >
                    <Switch id="isActive" />
                </Form.Item>

                <Title level={4}>Contacts</Title>
                <Form.List name="vendorContacts">
                    {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'phoneNumber']}
                                        rules={[{ required: true, message: 'Missing phone number' }]}
                                    >
                                        <Input id={`phoneNumber-${name}`} placeholder="Phone Number" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'email']}
                                        rules={[{ required: true, message: 'Missing email' }]}
                                    >
                                        <Input id={`email-${name}`} placeholder="Email" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'isPrimary']}
                                        valuePropName="checked"
                                    >
                                        <Switch id={`isPrimaryContact-${name}`} checkedChildren="Primary" unCheckedChildren="Secondary" />
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(name)} />
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Contact
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>

                <Title level={4}>Addresses</Title>
                <Form.List name="vendorAddresses">
                    {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'houseNo']}
                                        rules={[{ required: true, message: 'Missing house number' }]}
                                    >
                                        <Input id={`houseNo-${name}`} placeholder="House Number" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'street']}
                                        rules={[{ required: true, message: 'Missing street' }]}
                                    >
                                        <Input id={`street-${name}`} placeholder="Street" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'city']}
                                        rules={[{ required: true, message: 'Missing city' }]}
                                    >
                                        <Input id={`city-${name}`} placeholder="City" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'province']}
                                        rules={[{ required: true, message: 'Missing province' }]}
                                    >
                                        <Input id={`province-${name}`} placeholder="Province" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'country']}
                                        rules={[{ required: true, message: 'Missing country' }]}
                                    >
                                        <Input id={`country-${name}`} placeholder="Country" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'isPrimary']}
                                        valuePropName="checked"
                                    >
                                        <Switch id={`isPrimaryAddress-${name}`} checkedChildren="Primary" unCheckedChildren="Secondary" />
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(name)} />
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Address
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form>
        </Edit>
    );
}; 